import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Flame, ChevronLeft, ChevronRight, Building2, Home, Factory, CheckCircle2, Truck, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from '../components/ObfuscatedPhone';
import { ObfuscatedWeldingEmail } from '../components/ObfuscatedWeldingEmail';
import { GoogleBusinessSection } from '../components/GoogleBusiness';

// Structural Welding images
const STRUCTURAL_IMAGES = [
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/7itp1u75_D3E9DA83-B44C-473A-ABE1-4A713673A0E5_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/23rxibmj_IMG_2570.jpeg',
  'https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/mfti8l92_IMG_2097.jpeg',
  'https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/r8onigei_IMG_3873.jpeg',
  'https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/adb0trw1_IMG_2983.jpeg'
];

// Mobile Installation images
const MOBILE_INSTALL_IMAGES = [
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/6m2u0uck_348358884_630038155815666_2510261272305635035_n-e1686078098473.webp",
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/ucsrkbpf_escaliers-appartements-montreal-fer-forge.webp",
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/j415eey7_fabrication-escalier-couverture.jpg",
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/iffdgy33_fabrication-escalier-exterieur-montreal-4.jpg",
  "https://customer-assets.emergentagent.com/job_c6116862-128d-4497-b9aa-507466f1b8a3/artifacts/mqezthpo_fabrication-escalier-exterieur-montreal-1.jpg"
];

// Planning & Fabrication images
const FABRICATION_IMAGES = [
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/f75ng0f1_cover-fabrication-welding.jpg'
];

export default function Welding() {
  const { language, getLocalizedLink } = useLanguage();
  const [activeImage, setActiveImage] = useState(0);
  const [activeFabImage, setActiveFabImage] = useState(0);
  const [activeMobileImage, setActiveMobileImage] = useState(0);

  const nextMobileImage = () => {
    setActiveMobileImage((prev) => (prev + 1) % MOBILE_INSTALL_IMAGES.length);
  };

  const prevMobileImage = () => {
    setActiveMobileImage((prev) => (prev - 1 + MOBILE_INSTALL_IMAGES.length) % MOBILE_INSTALL_IMAGES.length);
  };

  const content = {
    en: {
      tagline: 'Kenscho Welding',
      title1: 'Welding',
      title2: 'Services',
      desc: 'Kenscho Welding provides structural welding and fabrication services for construction, industrial, and custom projects from Rawdon and Saint-Calixte, to St-Jérôme and Saint-Sauveur. From structural steel to precision repairs, we deliver quality welds every time.',
      
      // Mobile Welding Banner
      mobileWeldingTitle: 'Mobile Welding Services',
      mobileWeldingDesc: 'Kenscho Welding brings the shop to you. Available for emergencies 24/7 and planned, specialized on-site services for commercial, residential, and industrial projects in the Lanaudière, Laurentians, Laval, and Montreal areas.',
      mobileWeldingFeatures: [
        '24/7 Emergency Response',
        'On-Site Repairs',
        'Commercial & Industrial',
        'Residential Projects'
      ],
      
      // Mobile Welding CTA
      mobileWeldingCta: 'Ready to get started?',
      mobileWeldingCtaDesc: 'Call Kenscho Welding now to schedule an appointment or get a quote on your next welding project.',
      mobileWeldingCtaButton: 'Get a Quote',
      
      // Structural Welding
      structuralTitle: 'Structural Welding',
      structuralSubtitle: 'Residential • Commercial • Industrial',
      structuralDesc: 'Structural welding is the backbone of any construction project. Our structurally certified welders bring precision and expertise to every project, in Mirabel, Lachute, or Mont-Tremblant. Whether you\'re reinforcing an existing structure or building new from the ground up, we understand that you expect the highest quality. Kenscho Welding handles projects of any scale—from residential renovations to large-scale industrial facilities. Trust us to deliver welds that meet code, pass inspection, and stand the test of time.',
      structuralServices: [
        { icon: 'home', label: 'Residential', desc: 'Beam installations, stair railings, and structural reinforcements for homes.' },
        { icon: 'building', label: 'Commercial', desc: 'Steel framing, support columns, and tenant improvement projects.' },
        { icon: 'factory', label: 'Industrial', desc: 'Heavy-duty fabrication, equipment repairs, and facility upgrades.' }
      ],
      structuralCapabilities: [
        'Certified structural welders',
        'Code compliant',
        'All project sizes',
        'Steel & aluminum',
        'Inspection ready'
      ],
      
      // Mobile Installation Section
      mobileInstallTitle: 'Mobile Installation & On-Site Repairs',
      mobileInstallSubtitle: 'Balconies • Stairs • Railings',
      mobileInstallDesc: 'From worn-out balcony railings to complete staircase replacements, our Kenscho Welding mobile team handles it all on-site. We specialize in balcony repairs, stair installations, and railing replacements for residential and commercial properties. Whether you need a quick repair or a full custom installation, we bring our expertise directly to your location—minimizing disruption and maximizing results.',
      mobileInstallFeatures: [
        'Balcony repairs & replacements',
        'Stair fabrication & installation',
        'Railing repairs & upgrades',
        'Code-compliant installations',
        'Custom designs available'
      ],
      
      getQuote: 'Get a Quote',
      
      // Planning & Fabrication
      fabricationTitle: 'Planning & Fabrication',
      fabricationSubtitle: 'Custom metalwork from concept to completion',
      fabricationDesc: 'Every great project starts with a solid plan. Our fabrication services take your ideas from blueprint to reality, whether it\'s custom railings, steel stairs, structural components, or specialty metalwork. We work closely with you to understand your vision, provide accurate quotes, and deliver precision-fabricated pieces that fit perfectly on site. From small custom jobs to large-scale production runs, our shop is equipped to handle it all.',
      fabricationCapabilities: [
        'Custom design',
        'Shop drawings',
        'Precision cutting',
        'MIG & TIG welding',
        'Finishing options',
        'Delivery & install'
      ]
    },
    fr: {
      tagline: 'Soudure Kenscho',
      title1: 'Services de',
      title2: 'Soudure',
      desc: 'Soudure Kenscho offre des services de soudure structurelle et de fabrication pour les projets de construction, industriels et sur mesure de Rawdon et Saint-Calixte, à St-Jérôme et Saint-Sauveur. De l\'acier de structure aux réparations de précision, nous livrons des soudures de qualité à chaque fois.',
      
      // Mobile Welding Banner
      mobileWeldingTitle: 'Services de Soudure Mobile',
      mobileWeldingDesc: 'Soudure Kenscho amène l\'atelier chez vous. Disponible pour les urgences 24/7 et les services spécialisés planifiés sur site pour les projets commerciaux, résidentiels et industriels dans les régions de Lanaudière, Laurentides, Laval et Montréal.',
      mobileWeldingFeatures: [
        'Intervention d\'urgence 24/7',
        'Réparations sur site',
        'Commercial & Industriel',
        'Projets résidentiels'
      ],
      
      // Mobile Welding CTA
      mobileWeldingCta: 'Prêt à commencer?',
      mobileWeldingCtaDesc: 'Appelez Soudure Kenscho maintenant pour planifier un rendez-vous ou obtenir une soumission pour votre prochain projet de soudure.',
      mobileWeldingCtaButton: 'Obtenir une soumission',
      
      // Structural Welding
      structuralTitle: 'Soudure Structurelle',
      structuralSubtitle: 'Résidentiel • Commercial • Industriel',
      structuralDesc: 'La soudure structurelle est la colonne vertébrale de tout projet de construction. Nos soudeurs certifiés en structure apportent précision et expertise à chaque projet, à Mirabel, Lachute ou Mont-Tremblant. Que vous renforciez une structure existante ou construisiez à partir de zéro, nous comprenons que vous attendez la plus haute qualité. Soudure Kenscho gère des projets de toute envergure—des rénovations résidentielles aux grandes installations industrielles. Faites-nous confiance pour des soudures conformes aux codes, qui passent l\'inspection et résistent à l\'épreuve du temps.',
      structuralServices: [
        { icon: 'home', label: 'Résidentiel', desc: 'Installations de poutres, rampes d\'escalier et renforcements structurels.' },
        { icon: 'building', label: 'Commercial', desc: 'Charpentes métalliques, colonnes de support et projets d\'amélioration.' },
        { icon: 'factory', label: 'Industriel', desc: 'Fabrication lourde, réparations d\'équipement et mises à niveau.' }
      ],
      structuralCapabilities: [
        'Soudeurs structurels certifiés',
        'Conforme aux codes',
        'Tous les projets',
        'Acier et aluminium',
        'Prêt pour inspection'
      ],
      
      // Mobile Installation Section
      mobileInstallTitle: 'Installation Mobile et Réparations Sur Site',
      mobileInstallSubtitle: 'Balcons • Escaliers • Rampes',
      mobileInstallDesc: 'Des rampes de balcon usées aux remplacements complets d\'escaliers, notre équipe mobile Soudure Kenscho gère tout sur place. Nous nous spécialisons dans les réparations de balcons, les installations d\'escaliers et les remplacements de rampes pour les propriétés résidentielles et commerciales. Que vous ayez besoin d\'une réparation rapide ou d\'une installation personnalisée complète, nous apportons notre expertise directement à votre emplacement—minimisant les perturbations et maximisant les résultats.',
      mobileInstallFeatures: [
        'Réparations et remplacements de balcons',
        'Fabrication et installation d\'escaliers',
        'Réparations et améliorations de rampes',
        'Installations conformes aux codes',
        'Designs personnalisés disponibles'
      ],
      
      getQuote: 'Obtenir un devis',
      
      // Planning & Fabrication
      fabricationTitle: 'Planification et Fabrication',
      fabricationSubtitle: 'Travail métallique sur mesure du concept à la réalisation',
      fabricationDesc: 'Tout grand projet commence par un plan solide. Nos services de fabrication transforment vos idées en réalité, qu\'il s\'agisse de rampes sur mesure, d\'escaliers en acier, de composants structurels ou de travaux métalliques spécialisés. Nous travaillons en étroite collaboration avec vous pour comprendre votre vision, fournir des devis précis et livrer des pièces fabriquées avec précision qui s\'adaptent parfaitement sur le site. Des petits travaux personnalisés aux grandes productions, notre atelier est équipé pour tout gérer.',
      fabricationCapabilities: [
        'Design sur mesure',
        'Dessins d\'atelier',
        'Coupe de précision',
        'Soudage MIG et TIG',
        'Options de finition',
        'Livraison et installation'
      ]
    }
  };

  const t = content[language];

  // SEO Structured Data for Welding Services
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Welding Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Kenscho Design",
      "image": "https://customer-assets.emergentagent.com/job_construction-hub-125/artifacts/hiq4o5va_Logo%20001%20-%20TR%20Kenscho%20Design.png",
      "telephone": "+1-514-291-3737",
      "email": "Soudure@Kenscho.ca",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "QC",
        "addressCountry": "CA"
      },
      "areaServed": [
        { "@type": "Place", "name": "Lanaudière" },
        { "@type": "Place", "name": "Laurentians" },
        { "@type": "Place", "name": "Laval" },
        { "@type": "Place", "name": "Montreal" }
      ],
      "priceRange": "$$"
    },
    "name": language === 'fr' ? "Services de Soudure" : "Welding Services",
    "description": language === 'fr' 
      ? "Services de soudure structurelle et de fabrication pour la construction, l'industrie et les projets personnalisés. Soudure mobile 24/7, réparations sur site, balcons, escaliers et rampes."
      : "Structural welding and fabrication services for construction, industrial, and custom projects. 24/7 mobile welding, on-site repairs, balconies, stairs, and railings.",
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 45.5017,
        "longitude": -73.5673
      },
      "geoRadius": "100000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": language === 'fr' ? "Services de Soudure" : "Welding Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": language === 'fr' ? "Soudure Structurelle" : "Structural Welding"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": language === 'fr' ? "Soudure Mobile 24/7" : "24/7 Mobile Welding"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": language === 'fr' ? "Installation de Balcons et Escaliers" : "Balcony & Stair Installation"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": language === 'fr' ? "Fabrication sur Mesure" : "Custom Fabrication"
          }
        }
      ]
    }
  };

  const getServiceIcon = (iconName) => {
    const iconClass = "h-5 w-5";
    switch(iconName) {
      case 'home': return <Home className={iconClass} />;
      case 'building': return <Building2 className={iconClass} />;
      case 'factory': return <Factory className={iconClass} />;
      default: return <Flame className={iconClass} />;
    }
  };

  const nextImage = () => setActiveImage((prev) => (prev + 1) % STRUCTURAL_IMAGES.length);
  const prevImage = () => setActiveImage((prev) => (prev - 1 + STRUCTURAL_IMAGES.length) % STRUCTURAL_IMAGES.length);

  return (
    <div className="grain-texture pt-20" data-testid="welding-page">
      <Helmet>
        <title>{language === 'fr' 
          ? 'Services de Soudure | Soudure Mobile 24/7 | Lanaudière, Laurentides, Laval, Montréal | Kenscho'
          : 'Welding Services | 24/7 Mobile Welding | Lanaudière, Laurentians, Laval, Montreal | Kenscho'}
        </title>
        <meta name="description" content={language === 'fr'
          ? 'Services de soudure structurelle et mobile à Lanaudière, Laurentides, Laval et Montréal. De Rawdon à St-Jérôme, Mirabel, Lachute et Mont-Tremblant. Soudure résidentielle, commerciale et industrielle. Balcons, escaliers, rampes. Urgences 24/7. RBQ 5829 4596 01.'
          : 'Structural and mobile welding services in Lanaudière, Laurentians, Laval, and Montreal. From Rawdon to St-Jérôme, Mirabel, Lachute, and Mont-Tremblant. Residential, commercial & industrial welding. Balconies, stairs, railings. 24/7 emergencies. RBQ 5829 4596 01.'} />
        <meta name="keywords" content={language === 'fr'
          ? 'soudure, soudure mobile, soudeur Montreal, soudure structurelle, fabrication métallique, balcons, escaliers, rampes, soudure 24/7, soudure urgence, Lanaudière, Laurentides, Laval, Rawdon, Saint-Calixte, St-Jérôme, Saint-Sauveur, Mirabel, Lachute, Mont-Tremblant, réparation soudure'
          : 'welding, mobile welding, welder Montreal, structural welding, metal fabrication, balconies, stairs, railings, 24/7 welding, emergency welding, Lanaudière, Laurentians, Laval, Rawdon, Saint-Calixte, St-Jerome, Saint-Sauveur, Mirabel, Lachute, Mont-Tremblant, welding repair'} />
        <link rel="canonical" href={`https://www.kenscho.ca${getLocalizedLink('/welding')}`} />
        
        {/* Hreflang Tags for SEO */}
        <link rel="alternate" hreflang="en" href="https://www.kenscho.ca/welding" />
        <link rel="alternate" hreflang="fr" href="https://www.kenscho.ca/fr/welding" />
        <link rel="alternate" hreflang="x-default" href="https://www.kenscho.ca/welding" />
        
        {/* Open Graph */}
        <meta property="og:title" content={language === 'fr' ? 'Services de Soudure | Soudure Mobile 24/7 | Kenscho Design' : 'Welding Services | 24/7 Mobile Welding | Kenscho Design'} />
        <meta property="og:description" content={language === 'fr'
          ? 'Services de soudure structurelle et mobile professionnels. Résidentiel, commercial, industriel. Balcons, escaliers, rampes. Urgences 24/7.'
          : 'Professional structural and mobile welding services. Residential, commercial, industrial. Balconies, stairs, railings. 24/7 emergencies.'} />
        <meta property="og:url" content={`https://www.kenscho.ca${getLocalizedLink('/welding')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta property="og:locale:alternate" content={language === 'fr' ? 'en_CA' : 'fr_CA'} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/7itp1u75_D3E9DA83-B44C-473A-ABE1-4A713673A0E5_1_105_c.jpeg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={language === 'fr' ? 'Services de Soudure | Kenscho Design' : 'Welding Services | Kenscho Design'} />
        <meta name="twitter:description" content={language === 'fr'
          ? 'Soudure structurelle et mobile. Résidentiel, commercial, industriel. Urgences 24/7.'
          : 'Structural and mobile welding. Residential, commercial, industrial. 24/7 emergencies.'} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/7itp1u75_D3E9DA83-B44C-473A-ABE1-4A713673A0E5_1_105_c.jpeg" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="CA-QC" />
        <meta name="geo.placename" content="Lanaudière, Laurentians, Laval, Montreal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      {/* Hero */}
      <section className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8 animate-fade-in-up">
            <img 
              src="https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/cihcldgh_Logo%20SQ%20wh%20001%20-%20Kenscho%20Soudure-03.png" 
              alt={language === 'fr' ? 'Soudure Kenscho' : 'Kenscho Welding'}
              className="h-20 md:h-24 w-auto"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6 animate-fade-in-up animation-delay-100">
            {t.title1} <span className="text-secondary">{t.title2}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl animate-fade-in-up animation-delay-200">
            {t.desc}
          </p>
        </div>
      </section>

      {/* Mobile Welding Services Banner */}
      <section className="py-16 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                  <Truck className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-3xl md:text-4xl font-light tracking-tight">
                  {t.mobileWeldingTitle}
                </h2>
              </div>
              <p className="text-lg text-white/80 leading-relaxed">
                {t.mobileWeldingDesc}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {t.mobileWeldingFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/10 rounded-lg p-4">
                  <CheckCircle2 className="h-5 w-5 text-secondary shrink-0" />
                  <span className="text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Welding CTA */}
      <section className="py-12 bg-secondary/10 border-t border-secondary/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-light mb-2">{t.mobileWeldingCta}</h3>
              <p className="text-muted-foreground">{t.mobileWeldingCtaDesc}</p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <Phone className="h-5 w-5 text-secondary flex-shrink-0" />
                  <ObfuscatedPhone className="font-medium whitespace-nowrap" />
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-secondary flex-shrink-0" />
                  <ObfuscatedWeldingEmail className="font-medium" />
                </div>
              </div>
              <Button asChild className="rounded-full btn-animate">
                <Link to={getLocalizedLink('/contact')}>
                  {t.mobileWeldingCtaButton} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Structural Welding - Bento Style Layout */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-2">
              {t.structuralTitle}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t.structuralSubtitle}
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Image - Large */}
            <div className="lg:col-span-2">
              <div className="relative group">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-muted">
                  <img 
                    src={STRUCTURAL_IMAGES[activeImage]} 
                    alt={t.structuralTitle}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                {/* Navigation Arrows */}
                <button 
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button 
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-4 right-4 bg-black/50 text-white text-sm px-3 py-1.5 rounded-full">
                  {activeImage + 1} / {STRUCTURAL_IMAGES.length}
                </div>
              </div>
              
              {/* Thumbnails */}
              <div className="flex justify-center gap-2 mt-4">
                {STRUCTURAL_IMAGES.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                      index === activeImage 
                        ? 'border-secondary ring-2 ring-secondary/30' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`Structural welding ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Right Column - Description */}
            <div className="flex flex-col">
              {/* Description Card */}
              <div className="bg-card border border-border/60 rounded-2xl p-6 flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  {t.structuralDesc}
                </p>
              </div>
            </div>
          </div>

          {/* Capabilities - Full Width */}
          <div className="bg-secondary/10 rounded-2xl p-6 mt-6">
            <div className="flex flex-wrap gap-3 justify-center">
              {t.structuralCapabilities.map((cap, idx) => (
                <span 
                  key={idx}
                  className="px-4 py-2 bg-background border border-border/60 rounded-full text-sm"
                >
                  {cap}
                </span>
              ))}
            </div>
          </div>

          {/* Service Cards - Horizontal */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            {t.structuralServices.map((service, idx) => (
              <div 
                key={idx}
                className="bg-card border border-border/60 rounded-xl p-5 hover:border-secondary/50 transition-colors group"
              >
                <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center mb-3 group-hover:bg-secondary/20 transition-colors">
                  <span className="text-secondary">{getServiceIcon(service.icon)}</span>
                </div>
                <h4 className="font-medium mb-1">{service.label}</h4>
                <p className="text-sm text-muted-foreground">{service.desc}</p>
              </div>
            ))}
          </div>

          {/* Serving Areas Bubbles */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-secondary" />
              <span className="text-sm">{language === 'fr' ? 'Nous desservons' : 'Serving'}</span>
            </div>
            {(language === 'fr' 
              ? ['Lanaudière', 'Laurentides', 'Laval', 'Montréal']
              : ['Lanaudière', 'Laurentians', 'Laval', 'Montreal']
            ).map((area) => (
              <span key={area} className="text-sm px-3 py-1 bg-muted rounded-full">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Installation & On-Site Repairs - Mirrored Layout */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-2">
              {t.mobileInstallTitle}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t.mobileInstallSubtitle}
            </p>
          </div>

          {/* Bento Grid - Mirrored (Text Left, Image Right) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Description */}
            <div className="flex flex-col order-2 lg:order-1">
              {/* Description Card */}
              <div className="bg-card border border-border/60 rounded-2xl p-6 flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  {t.mobileInstallDesc}
                </p>
              </div>
            </div>

            {/* Main Image - Large with Carousel */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <div className="relative group">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-muted">
                  <img 
                    src={MOBILE_INSTALL_IMAGES[activeMobileImage]} 
                    alt={t.mobileInstallTitle}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                {/* Navigation Arrows */}
                <button 
                  onClick={prevMobileImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button 
                  onClick={nextMobileImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-4 right-4 bg-black/50 text-white text-sm px-3 py-1.5 rounded-full">
                  {activeMobileImage + 1} / {MOBILE_INSTALL_IMAGES.length}
                </div>
              </div>
              
              {/* Thumbnails */}
              <div className="flex justify-center gap-2 mt-4">
                {MOBILE_INSTALL_IMAGES.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveMobileImage(index)}
                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                      index === activeMobileImage 
                        ? 'border-secondary ring-2 ring-secondary/30' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`Mobile installation ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Features - Full Width */}
          <div className="bg-secondary/10 rounded-2xl p-6 mt-6">
            <div className="flex flex-wrap gap-3 justify-center">
              {t.mobileInstallFeatures.map((feature, idx) => (
                <span 
                  key={idx}
                  className="px-4 py-2 bg-background border border-border/60 rounded-full text-sm"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Planning & Fabrication Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-2">
              {t.fabricationTitle}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t.fabricationSubtitle}
            </p>
          </div>

          {/* Bento Grid - Image Left, Text Right (like Structural Welding) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Image - Large */}
            <div className="lg:col-span-2 relative group">
              <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-muted">
                <img 
                  src={FABRICATION_IMAGES[activeFabImage]} 
                  alt={t.fabricationTitle}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Right Column - Description */}
            <div className="flex flex-col">
              {/* Description Card */}
              <div className="bg-card border border-border/60 rounded-2xl p-6 flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  {t.fabricationDesc}
                </p>
              </div>
            </div>
          </div>

          {/* Capabilities - Full Width */}
          <div className="bg-secondary/10 rounded-2xl p-6 mt-6">
            <div className="flex flex-wrap gap-3 justify-center">
              {t.fabricationCapabilities.map((cap, idx) => (
                <span 
                  key={idx}
                  className="px-4 py-2 bg-background border border-border/60 rounded-full text-sm"
                >
                  {cap}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Button asChild size="lg" className="rounded-full">
              <Link to={getLocalizedLink('/contact')}>
                {t.getQuote} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Google Business Section - Soudure Kenscho */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <GoogleBusinessSection profile="welding" showMap={true} />
        </div>
      </section>
    </div>
  );
}
